import React from "react";
import { log, LogLevel } from "utils/LogUtil";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import { COLOR_SYSTEM } from "components/design/design-system";
import { AppDispatch } from "store";
import {
  fetchPatientPrescriptions,
  fetchPatientOTC,
  fetchPatientHFF,
} from "store/middleware";
import DrugIdentificationDetailPopup from "pages/Admin/DrugIdentifications/DrugIdentificationDetailPopup";
import ConsultationRecordDetail from "components/organisms/ConsultationRecordDetail";

type FcmMessageType =
  | "NHIS_DELIVERED"
  | "NHIS_DATA_FETCHED"
  | "NHIS_AUTH_FAILED"
  | "consultation_completed"
  | "CONSULTATION_COMPLETED"
  | "consultation_failed"
  | "CONSULTATION_FAILED"
  | "DRUG_IDENTIFICATION_COMPLETED"
  | "DRUG_IDENTIFICATION_FAILED";

interface FcmMessageData {
  type: FcmMessageType;
  [key: string]: string;
}

const NHIS_TOAST_STYLE: React.CSSProperties = {
  backgroundColor: COLOR_SYSTEM.get("Blue")?.[50],
  border: `1px solid ${COLOR_SYSTEM.get("Blue")?.[200]}`,
  boxShadow: "0 8px 24px rgba(39, 54, 72, 0.12)",
  padding: "16px 48px 16px 24px",
  minWidth: "360px",
  borderRadius: "12px",
};

const CONSULTATION_TOAST_STYLE: React.CSSProperties = {
  backgroundColor: COLOR_SYSTEM.get("Purple")?.[50],
  border: `1px solid ${COLOR_SYSTEM.get("Purple")?.[200]}`,
  boxShadow: "0 8px 24px rgba(39, 54, 72, 0.12)",
  padding: "16px 48px 16px 24px",
  minWidth: "360px",
  borderRadius: "12px",
};

const NHIS_ERROR_TOAST_STYLE: React.CSSProperties = {
  backgroundColor: COLOR_SYSTEM.get("Red")?.[50],
  border: `1px solid ${COLOR_SYSTEM.get("Red")?.[200]}`,
  boxShadow: "0 8px 24px rgba(39, 54, 72, 0.12)",
  padding: "16px 48px 16px 24px",
  minWidth: "360px",
  borderRadius: "12px",
};

const DRUG_TOAST_STYLE: React.CSSProperties = {
  backgroundColor: COLOR_SYSTEM.get("Green")?.[50],
  border: `1px solid ${COLOR_SYSTEM.get("Green")?.[200]}`,
  boxShadow: "0 8px 24px rgba(39, 54, 72, 0.12)",
  padding: "16px 48px 16px 24px",
  minWidth: "360px",
  borderRadius: "12px",
};

interface FcmNotification {
  title?: string;
  body?: string;
}

export const handleFcmMessage = (
  data: FcmMessageData,
  navigate: (path: string) => void,
  dispatch: AppDispatch,
  notification?: FcmNotification
): void => {
  switch (data.type) {
    // === NHIS ===
    case "NHIS_DELIVERED": {
      const patientId = Number(data.patientId);
      ToastManager.show({
        content: notification?.body || `${data.patientName || "환자"}님의 처방전 정보가 저장되었습니다.`,
        duration: 60000,
        position: "top-right",
        style: NHIS_TOAST_STYLE,
        onClick: () => navigate(`/patients/${patientId}`),
      });
      dispatch(fetchPatientPrescriptions(patientId));
      dispatch(fetchPatientOTC(patientId));
      dispatch(fetchPatientHFF(patientId));
      window.dispatchEvent(
        new CustomEvent("nhis-status-update", { detail: { patientId: data.patientId } })
      );
      break;
    }

    case "NHIS_DATA_FETCHED": {
      const patientId = Number(data.patientId);
      dispatch(fetchPatientPrescriptions(patientId));
      dispatch(fetchPatientOTC(patientId));
      dispatch(fetchPatientHFF(patientId));
      window.dispatchEvent(
        new CustomEvent("nhis-status-update", { detail: { patientId: data.patientId } })
      );
      break;
    }

    case "NHIS_AUTH_FAILED":
      ToastManager.show({
        content: notification?.body || `${data.patientName || "환자"}님의 본인인증이 실패했습니다.`,
        duration: 60000,
        position: "top-right",
        style: NHIS_ERROR_TOAST_STYLE,
        onClick: () => navigate(`/patients/${data.patientId}`),
      });
      window.dispatchEvent(
        new CustomEvent("nhis-status-update", { detail: { patientId: data.patientId } })
      );
      break;

    // === 상담 (과도기 소문자 호환) ===
    case "consultation_completed":
    case "CONSULTATION_COMPLETED":
      ToastManager.show({
        content: "상담 기록 처리가 완료되었습니다.",
        duration: 10000,
        position: "top-right",
        style: CONSULTATION_TOAST_STYLE,
        onClick: () => {
          const recordId = Number(data.recordId);
          if (recordId) {
            SlideManager.show({
              body: <ConsultationRecordDetail recordId={recordId} />,
              direction: "right",
              needCloseButton: true,
            });
          }
        },
      });
      break;

    case "consultation_failed":
    case "CONSULTATION_FAILED":
      ToastManager.show({
        content: "상담 기록 처리에 실패했습니다.",
        duration: 10000,
        position: "top-right",
        style: CONSULTATION_TOAST_STYLE,
      });
      break;

    // === 의약품 식별 ===
    case "DRUG_IDENTIFICATION_COMPLETED":
      ToastManager.show({
        content: "의약품 식별이 완료되었습니다.",
        duration: 10000,
        position: "top-right",
        style: DRUG_TOAST_STYLE,
        onClick: () => {
          const identificationId = Number(data.identificationId);
          if (identificationId) {
            SlideManager.show({
              body: <DrugIdentificationDetailPopup identificationId={identificationId} />,
              direction: "right",
              width: "800px",
              needCloseButton: true,
            });
          }
        },
      });
      break;

    case "DRUG_IDENTIFICATION_FAILED":
      ToastManager.show({
        content: "의약품 식별에 실패했습니다.",
        duration: 10000,
        position: "top-right",
        style: DRUG_TOAST_STYLE,
      });
      break;

    default:
      log(LogLevel.WARNING, "알 수 없는 FCM 메시지 타입:", data.type);
  }
};
